import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LinkIcon, LinkBreakIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MotiveLinkDialog({ truck, open, onOpenChange, onLinked }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSelected(truck?.motive_vehicle_id || "");
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/integrations/motive/vehicles");
        setVehicles(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load Motive vehicles");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, truck]);

  const save = async () => {
    try {
      await api.post(`/trucks/${truck.id}/link-motive`, { motive_vehicle_id: selected || null });
      toast.success(selected ? "Truck linked to Motive" : "Truck unlinked");
      onOpenChange(false);
      onLinked && onLinked();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const unlink = async () => {
    try {
      await api.post(`/trucks/${truck.id}/link-motive`, { motive_vehicle_id: null });
      toast.success("Unlinked");
      onOpenChange(false);
      onLinked && onLinked();
    } catch (e) {
      toast.error("Failed");
    }
  };

  // Suggest match by VIN
  const vinMatch = vehicles.find((v) => v.vin && truck?.vin && v.vin.toLowerCase() === truck.vin.toLowerCase());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="motive-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Link to Motive</DialogTitle>
        </DialogHeader>

        {error && (
          <div className="border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2 rounded-sm" data-testid="motive-link-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="font-mono text-sm text-slate-500 py-4">LOADING VEHICLES...</div>
        ) : vehicles.length === 0 && !error ? (
          <div className="text-sm text-slate-600 py-4">No vehicles returned by Motive.</div>
        ) : (
          <>
            {vinMatch && selected !== vinMatch.motive_vehicle_id && (
              <button
                type="button"
                onClick={() => setSelected(vinMatch.motive_vehicle_id)}
                className="w-full text-left border border-blue-300 bg-blue-50 rounded-sm px-3 py-2 hover:bg-blue-100 transition-colors duration-150"
                data-testid="vin-match-suggestion"
              >
                <div className="text-[10px] uppercase tracking-widest font-mono text-blue-700 font-bold">/// VIN match found</div>
                <div className="text-sm font-semibold mt-0.5">{vinMatch.number} — {vinMatch.make} {vinMatch.model}</div>
              </button>
            )}

            <div>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger className="rounded-sm" data-testid="motive-vehicle-select"><SelectValue placeholder="Select a Motive vehicle to link" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.motive_vehicle_id} value={v.motive_vehicle_id}>
                      {v.number || `Vehicle ${v.motive_vehicle_id}`} — {[v.make, v.model, v.year].filter(Boolean).join(" ")} {v.vin ? `(${v.vin})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1.5 font-mono">{vehicles.length} vehicles available</p>
            </div>
          </>
        )}

        <DialogFooter>
          {truck?.motive_vehicle_id && (
            <Button type="button" variant="outline" onClick={unlink} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 mr-auto" data-testid="motive-unlink">
              <LinkBreakIcon size={14} className="mr-2" /> Unlink
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={!selected} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="motive-link-save">
            <LinkIcon size={14} className="mr-2" /> Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
